/**
 * Application-level i18n built on `i18next` + `react-i18next` (see `i18n.ts`
 * for the instance setup). Translations live in the JSON files under
 * `shared/i18n/locales/` — edit those to change any string; no code changes
 * needed (and the `lib.*` section feeds the library's strings too).
 *
 * `locale` remains app Redux state (`localeSlice`, FR-A8): the
 * `LanguageSwitcher` dispatches it for number formatting
 * (`Intl` via `selectLocale`) and calls `i18n.changeLanguage` so every
 * `useTranslation` consumer re-renders.
 */

import { useTranslation as useI18nextTranslation } from "react-i18next";
import { useAppSelector } from "@/store/hooks";
import type { RootState } from "@/store/store";

/** i18next instance + initializer (JSON resources, library provider). */
export { i18n, initI18n } from "./i18n";

/** Supported languages (BCP 47 tags; `SUPPORTED_LOCALES` in constants mirrors this). */
export type AppLocaleTag = "en-US" | "ja-JP" | "fr-FR";

/** Primary-language subtag the i18next resources are keyed by. */
export type AppLocale = "en" | "ja" | "fr";

/** Switcher entries: tag + native label (never translated). */
export const SUPPORTED_LANGUAGES: ReadonlyArray<{
  tag: AppLocaleTag;
  label: string;
}> = [
  { tag: "en-US", label: "English" },
  { tag: "ja-JP", label: "日本語" },
  { tag: "fr-FR", label: "Français" },
];

export type AppMessageKey =
  // navigation
  | "nav.dashboard"
  | "nav.basicInfoHd"
  | "nav.oee"
  | "nav.paperUsage"
  | "nav.printOverview"
  // serial filter (app toolbar)
  | "serials.title"
  | "serials.filterTitle"
  | "serials.filterDescription"
  | "serials.searchPlaceholder"
  | "serials.searchLabel"
  | "serials.noMatch"
  | "serials.done"
  | "serials.selectedCount"
  | "serials.checkAll"
  | "serials.groupToggle"
  // widget frame states
  | "widget.noData"
  | "widget.failedToLoad"
  | "widget.enterFullscreen"
  | "widget.exitFullscreen"
  | "widget.exitFullscreenHint"
  | "widget.fullscreen"
  // toolbar
  | "toolbar.sort"
  | "toolbar.sortBy"
  | "toolbar.sortTitle"
  | "toolbar.drillUp"
  | "toolbar.drillDown"
  | "toolbar.export"
  | "toolbar.exportTitle"
  | "toolbar.exportDescription"
  | "toolbar.exportCsv"
  | "toolbar.exportCsvDescription"
  | "toolbar.exportExcel"
  | "toolbar.exportExcelDescription"
  | "toolbar.exportPng"
  | "toolbar.exportPngDescription"
  | "toolbar.close"
  | "toolbar.reset"
  | "toolbar.refresh"
  // loading
  | "loading.data"
  | "loading.moreData"
  // dashboard
  | "kpi.inkUsedMock";

/**
 * Translate function shape used across widget configs and components:
 * keys are JSON paths (`charts.title.oeeTrend`, `columns.email`, …) — the
 * `AppMessageKey` union above only documents the framework chrome keys;
 * business keys live in the same JSON files and stay plain strings so new
 * widgets can add keys without touching this module.
 */
export type TranslateFn = (key: string, params?: MessageParams) => string;

/** Convenience selector for the current locale tag. */
export const selectLocale = (state: RootState): AppLocaleTag =>
  state.locale.value;

/** Interpolation params for `{name}`/`{{name}}` placeholders. */
export interface MessageParams {
  [param: string]: string | number | undefined;
}

/**
 * React binding over react-i18next: returns the Redux locale tag (for
 * `Intl` formatting calls) and a `t` bound to the i18next language. Both
 * update reactively when the language changes.
 */
export function useTranslation(): {
  locale: AppLocaleTag;
  t: TranslateFn;
} {
  const locale = useAppSelector(selectLocale);
  const { t } = useI18nextTranslation();
  return {
    locale,
    // i18next's detailed return type collapses to `string` for these keys.
    t: (key, params) => t(key, params as never) as unknown as string,
  };
}
